<script setup>
  import {inject, onMounted, reactive, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {router} from "@inertiajs/vue3";
  import {sharedrecordStatus} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "RecordComponentEditAgainDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    sharedata: {
      type: Object,
      required: true,
    },
  });

  const broadcast = inject("$broadcast");

  const refEditAgainDialog = ref(null);
  const refConfirmRecordOverwriteDialog = ref(null);
  const allowOverwriteRecord = ref(false);

  const state = reactive({
    loading: false,
  });

  onMounted(() => {
    if (props.sharedata.cannot_edit_again_reason === null) {
      // not a shared record, so always allow overwrite
      allowOverwriteRecord.value = true;
    }

    if (
      (props.sharedata.shared_owners ?? []).length === 1 &&
      props.sharedata.shared_owners[0].copied_record_id !== props.record.id
    ) {
      // this record is already one of my variants from the
      // record which was originally shared with me
      allowOverwriteRecord.value = true;
    }
  });

  const open = () => {
    state.loading = false;
    refEditAgainDialog.value.open();
  };

  const close = () => {
    refEditAgainDialog.value.close();
  };

  const performRecordAction = (action) => {
    state.loading = true;

    router.put(
      route("records.update", {patient: props.patient.id, record: props.record.id}),
      {
        action: action,
      },
      {
        preserveScroll: action === "reset",
        preserveState: false,
        onSuccess: () => {
          broadcast.record.postMessage(props.record.id, {
            action: action, // "duplicate" or "reset"
          });
        },
        onError: () => {
          state.loading = false;
        },
      },
    );
  };

  const duplicateRecord = () => {
    performRecordAction("duplicate");
  };

  const openConfirmRecordOverwriteDialog = () => {
    refConfirmRecordOverwriteDialog.value.open();
    close();
  };

  const overwriteRecord = () => {
    open();
    performRecordAction("reset");
  };

  const cancelOverwriteRecord = () => {
    abortRouter();
    open();
  };

  const abortRouter = () => {
    router.cancelAll();
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refEditAgainDialog"
    dialog-title="Variante bearbeiten"
    dialog-type="abort"
    :dialog-width="state.loading ? 'small' : 'medium'"
    :test-id="getTestId('edit-again')"
    @dialog-closed="abortRouter()"
  >
    <template #content>
      <div v-if="state.loading" class="flex h-20 items-center justify-center">
        <MedSpinner size="xl" :data-test="getTestId('edit-again-dialog-spinner')" />
      </div>

      <template v-else>
        <ComponentAlert v-if="!!record.closed" type="error" :test-id="getTestId('edit-again-dialog')">
          Sie haben diese Variante bereits als abgeschlossen gekennzeichnet.
        </ComponentAlert>

        <!-- Record not shared to others and not shared with user -->
        <template v-if="allowOverwriteRecord">
          <p>Sie haben zwei Möglichkeiten, diese Variante zu bearbeiten:</p>

          <ol class="mt-4 list-decimal pl-5">
            <li>
              <p>
                <strong class="font-bold">
                  Neue Variante für
                  <div class="mx-0.5 inline-block text-lg text-gray-900">
                    <span class="font-semibold" :data-encrypted="patient.firstname" />
                    <span class="font-semibold" :data-encrypted="patient.lastname" />
                  </div>
                  anlegen
                </strong>
              </p>

              <p class="leading-snug">
                Sie erstellen eine neue zusätzliche Variante. Ihre bisherige Bearbeitung wird übernommen.
              </p>

              <p class="mt-2">
                <MedButton
                  variant="primary"
                  :data-test="getTestId('edit-again-dialog-duplicate-button')"
                  @click="duplicateRecord()"
                >
                  Neue Variante anlegen
                </MedButton>
              </p>
            </li>

            <li class="mt-8">
              <p>
                <strong class="font-bold">
                  Aktuelle Variante für
                  <div class="mx-0.5 inline-block text-lg text-gray-900">
                    <span class="font-semibold" :data-encrypted="patient.firstname" />
                    <span class="font-semibold" :data-encrypted="patient.lastname" />
                  </div>
                  überschreiben
                </strong>
              </p>

              <p class="leading-snug">
                Dadurch haben Sie die Möglichkeit, die aktuelle Variante anzupassen. Ihre bisherige Bearbeitung wird
                übernommen.
              </p>

              <p class="mt-2">
                <MedButton
                  variant="primary"
                  :data-test="getTestId('edit-again-dialog-overwrite-button')"
                  @click="openConfirmRecordOverwriteDialog()"
                >
                  Analyseergebnis überschreiben
                </MedButton>
              </p>
            </li>
          </ol>
        </template>

        <!-- Record shared to others or shared with current user -->
        <template v-else>
          <p v-if="sharedata.cannot_edit_again_reason === 'sharedByMe'">
            Da Sie diese Variante bereits über die Fallfreigabe mit einer anderen Person geteilt haben, können Sie nur
            eine neue Variante anlegen. Die aktuelle Variante inkl. bestehendem Analyseergebnis bleibt gespeichert und
            kann jederzeit erneut aufgerufen werden.
          </p>

          <p v-else-if="sharedata.cannot_edit_again_reason === sharedrecordStatus.active">
            Da diese Variante mit Ihnen geteilt wurde, können Sie nur eine neue Variante anlegen. Die aktuelle Variante
            inkl. bestehendem Analyseergebnis bleibt gespeichert und kann jederzeit erneut aufgerufen werden.
          </p>

          <p v-else-if="sharedata.cannot_edit_again_reason === sharedrecordStatus.completed">
            Da Sie diese geteilte Variante bereits an den Absender zurückgesendet haben, können Sie nur eine neue
            Variante anlegen. Die aktuelle Variante inkl. bestehendem Analyseergebnis wird gespeichert und kann
            jederzeit über die Patientenakte aufgerufen werden.
          </p>

          <p v-else>
            Sie können eine neue Variante anlegen, um diese weiterzubearbeiten. Die aktuelle Variante inkl. bestehendem
            Analyseergebnis wird gespeichert und kann jederzeit erneut aufgerufen werden.
          </p>
        </template>
      </template>
    </template>

    <template #actions>
      <template v-if="!allowOverwriteRecord">
        <MedButton
          variant="primary"
          :disabled="state.loading"
          :data-test="getTestId('edit-again-dialog-duplicate-button')"
          @click="duplicateRecord()"
        >
          Neue Variante anlegen
        </MedButton>
      </template>

      <MedButton :data-test="getTestId('edit-again-dialog-close-button')" @click="close()">Abbrechen</MedButton>
    </template>
  </ComponentDialog>

  <ComponentDialog
    ref="refConfirmRecordOverwriteDialog"
    dialog-title="Analyseergebnis überschreiben?"
    :test-id="getTestId('edit-again')"
    @primary-button-clicked="overwriteRecord()"
    @cancel-button-clicked="cancelOverwriteRecord()"
  >
    <template #content>Dabei wird das Analyseergebnis der aktuellen Variante unwiderruflich gelöscht.</template>
  </ComponentDialog>
</template>
